import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileEdit, Eye, Send, RotateCcw, X, Save } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  drip_free: "Free user drip",
  drip_paid: "Premium user drip",
  system: "System emails",
};

export default function EmailTemplateEditor() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body_html: "" });
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await api.get("/admin/email-templates");
      setTemplates(r.data);
    } catch (err) {
      toast.error("Could not load templates");
    }
  };

  const openEditor = (tpl) => {
    setSelected(tpl);
    setDraft({ subject: tpl.subject || "", body_html: tpl.body_html || "" });
    setShowPreview(false);
  };

  const saveDraft = async () => {
    if (!selected) return;
    if (!draft.subject.trim() || !draft.body_html.trim()) {
      toast.error("Subject and body cannot be empty.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.put(`/admin/email-templates/${selected.key}`, draft);
      toast.success("Template saved");
      setTemplates((prev) => prev.map((t) => t.key === selected.key ? r.data : t));
      setSelected(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const restoreDefault = async () => {
    if (!selected) return;
    if (!window.confirm("Restore this email to its original default copy? Your edits will be lost.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/email-templates/${selected.key}/restore`);
      toast.success("Restored to default");
      setTemplates((prev) => prev.map((t) => t.key === selected.key ? r.data : t));
      setSelected(r.data);
      setDraft({ subject: r.data.subject, body_html: r.data.body_html });
    } catch (err) {
      toast.error("Could not restore");
    } finally { setBusy(false); }
  };

  const sendPreview = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      // Save current draft first so the preview uses live edits
      if (draft.subject !== selected.subject || draft.body_html !== selected.body_html) {
        await saveDraft();
      }
      const r = await api.post(`/admin/email-templates/${selected.key}/preview`);
      toast.success(`Preview sent to ${r.data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send preview");
    } finally { setBusy(false); }
  };

  // Group by category
  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || "system";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  // Render preview body with sample placeholders
  const renderPreviewBody = (html) => {
    const sample = {
      first_name: "Sarah",
      name: "Sarah Johnson",
      email: "sarah@example.com",
      app_origin: window.location.origin,
      inviter_name: "Michael",
      chest_name: "Mom",
      role: "contributor",
      cta_url: window.location.origin + "/dashboard",
      magic_url: window.location.origin + "/magic-link?token=demo",
      name_if_known: " Sarah",
      referee_name: "Linda Thomas",
    };
    return (html || "").replace(/\{([a-z_][a-z0-9_]*)\}/g, (m, k) => sample[k] !== undefined ? sample[k] : m);
  };

  return (
    <div className="hc-card p-6 mb-8 print:hidden" data-testid="admin-email-editor">
      <div className="flex items-center gap-3 mb-4">
        <FileEdit className="w-5 h-5 text-[#3A5A4A]" />
        <h2 className="font-display text-xl">Email templates ({templates.length})</h2>
      </div>
      <p className="text-sm text-[#7A736E] mb-5">
        Edit any system email below. Use placeholders like <code className="px-1 bg-[#F3EFEA] rounded">{"{first_name}"}</code>,{" "}
        <code className="px-1 bg-[#F3EFEA] rounded">{"{app_origin}"}</code>, <code className="px-1 bg-[#F3EFEA] rounded">{"{chest_name}"}</code>. They&apos;ll be filled in automatically when each email is sent.
      </p>

      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat} className="mb-5">
          <p className="text-xs uppercase tracking-wider text-[#7A736E] mb-2">{CATEGORY_LABEL[cat] || cat}</p>
          <div className="space-y-1">
            {list.map((t) => (
              <button
                key={t.key}
                onClick={() => openEditor(t)}
                className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-[#F3EFEA] text-sm transition group"
                data-testid={`template-row-${t.key}`}
              >
                <span className="flex items-center gap-3 min-w-0">
                  {t.day_offset !== undefined && <span className="text-xs text-[#7A736E] w-12 flex-shrink-0">Day {t.day_offset}</span>}
                  <span className="truncate">
                    <span className="font-medium">{t.name}</span>
                    <span className="text-[#7A736E] hidden md:inline"> — {t.subject}</span>
                  </span>
                </span>
                <span className="text-xs text-[#3A5A4A] group-hover:underline flex-shrink-0">Edit →</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Modal editor */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="email-editor-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-[#EBE5DF] flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#7A736E]">{CATEGORY_LABEL[selected.category] || selected.category}{selected.day_offset !== undefined ? ` · Day ${selected.day_offset}` : ""}</p>
                <h3 className="font-display text-lg">{selected.name}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#7A736E] hover:text-[#3D3935]" data-testid="email-editor-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#7A736E]">Subject</label>
                <Input
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  className="mt-1.5 rounded-xl border-[#EBE5DF]"
                  data-testid="email-editor-subject"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs uppercase tracking-wider text-[#7A736E]">Body (HTML allowed)</label>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-xs text-[#3A5A4A] hover:underline flex items-center gap-1"
                    data-testid="email-editor-toggle-preview"
                  >
                    <Eye className="w-3 h-3" /> {showPreview ? "Edit HTML" : "Preview"}
                  </button>
                </div>
                {showPreview ? (
                  <div
                    className="rounded-xl border border-[#EBE5DF] p-4 bg-[#FCFAF8] min-h-[300px] max-h-[400px] overflow-y-auto prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: renderPreviewBody(draft.body_html) }}
                    data-testid="email-editor-preview"
                  />
                ) : (
                  <textarea
                    value={draft.body_html}
                    onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                    rows={16}
                    className="w-full px-4 py-3 rounded-xl border border-[#EBE5DF] focus:outline-none focus:border-[#3A5A4A] text-xs font-mono"
                    data-testid="email-editor-body"
                  />
                )}
              </div>
              {selected.updated_by && (
                <p className="text-xs text-[#7A736E]">
                  Last edited {selected.updated_at ? new Date(selected.updated_at).toLocaleString() : "—"} by {selected.updated_by}
                </p>
              )}
            </div>

            <div className="px-6 py-3 border-t border-[#EBE5DF] flex flex-wrap gap-2 justify-between items-center">
              <Button
                onClick={restoreDefault}
                disabled={busy}
                variant="outline"
                className="rounded-full border-[#EBE5DF] text-sm"
                data-testid="email-editor-restore"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore default
              </Button>
              <div className="flex gap-2">
                <Button
                  onClick={sendPreview}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full border-[#EBE5DF] text-sm"
                  data-testid="email-editor-send-preview"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Email me a preview
                </Button>
                <Button
                  onClick={saveDraft}
                  disabled={busy}
                  className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-sm"
                  data-testid="email-editor-save"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" /> Save changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
